import pluralize from "pluralize";

/**
 * Extracts object names from a workflow name
 *
 * Examples:
 * - "find egg" → ["egg"]
 * - "find egg and car" → ["egg", "car"]
 * - "find eggs and cars" → ["egg", "car"] (removes pluralization)
 * - "find egg 10" → ["egg"] (removes numbers)
 *
 * @param workflowName - The name of the workflow to parse
 * @returns An array of extracted object names, or null if none found
 */
export function extractObjectNamesFromWorkflow(
    workflowName: string | null | undefined
): string[] | null {
    if (!workflowName) return null;

    // Remove "find" and split by "and" and commas to get separate objects
    let text = workflowName.toLowerCase().replace(/^find\s+/, "");

    // Split by "and" and commas, then clean up each part
    const objects = text
        .split(/\s+and\s+|,\s*/)
        .map((obj) => {
            // Remove numbers and extra whitespace
            const cleaned = obj
                .trim()
                .replace(/\s*\d+\s*$/, "")
                .trim();

            // Use pluralize library to convert to singular form
            return pluralize.singular(cleaned);
        })
        .filter((obj) => obj.length > 0);

    return objects.length > 0 ? objects : null;
}

/**
 * Formats object names as a pluralized, readable string
 *
 * Examples:
 * - ["egg"] → "eggs"
 * - ["egg", "car"] → "eggs and cars"
 * - ["egg", "car", "dog"] → "eggs, cars, and dogs"
 *
 * @param objectNames - Array of object names
 * @returns A formatted, pluralized string, or null if no objects
 */
export function formatObjectNamesPluralized(objectNames: string[] | null): string | null {
    if (!objectNames || objectNames.length === 0) return null;

    if (objectNames.length === 1) {
        return pluralize.plural(objectNames[0]);
    }

    if (objectNames.length === 2) {
        return `${pluralize.plural(objectNames[0])} and ${pluralize.plural(objectNames[1])}`;
    }

    // For 3+ objects, use commas with "and" before the last one
    const pluralized = objectNames.map((name) => pluralize.plural(name));
    const lastObject = pluralized.pop();
    return `${pluralized.join(", ")}, and ${lastObject}`;
}
